

import { useState, useEffect } from "react";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { useTaskStore, Task, TaskStatus } from "@/stores/taskStore";
import { api } from "@/lib/api-client";
import { KanbanColumn } from "./KanbanColumn";
import { TaskCard } from "./TaskCard";
import { TaskDialog } from "./TaskDialog";
import { ProjectsMenu } from "./ProjectsMenu";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";

const COLUMNS: { id: TaskStatus; title: string }[] = [
  { id: "PENDING", title: "Pendientes" },
  { id: "IN_PROGRESS", title: "En Ejecución" },
  { id: "IN_REVIEW", title: "En Prueba" },
  { id: "COMPLETED", title: "Terminadas" },
];

export function KanbanBoard() {
  const { tasks, projects, selectedProjectId, setTasks, setProjects, setSelectedProjectId, updateTask } = useTaskStore();
  const [activeTask, setActiveTask] = useState<Task | null>(null);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadData();
  }, [selectedProjectId, refreshKey]);

  const loadData = async () => {
    try {
      const [tasksData, projectsData] = await Promise.all([
        api.get<{ success: boolean; tasks: Task[] }>(`/tasks${selectedProjectId ? `?projectId=${selectedProjectId}` : ""}`),
        api.get<{ success: boolean; projects: any[] }>('/projects'),
      ]);

      if (tasksData.success) {
        setTasks(tasksData.tasks);
      }
      if (projectsData.success) {
        setProjects(projectsData.projects);
        if (!selectedProjectId && projectsData.projects.length > 0) {
          setSelectedProjectId(projectsData.projects[0].id);
        }
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const task = tasks.find((t) => t.id === event.active.id);
    if (task) {
      setActiveTask(task);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTask(null);

    if (!over) return;

    const taskId = active.id as string;
    
    // Determinar el nuevo estado: puede ser la columna (over.id = TaskStatus) 
    // o una tarea en otra columna (obtener el status de esa tarea)
    let newStatus: TaskStatus | null = null;
    
    // Si over.id es uno de los estados válidos, usarlo directamente
    if (COLUMNS.find(c => c.id === over.id)) {
      newStatus = over.id as TaskStatus;
    } else {
      // Si es una tarea, obtener el status de esa tarea
      const overTask = tasks.find(t => t.id === over.id);
      if (overTask) {
        newStatus = overTask.status;
      }
    }

    if (!newStatus) return;

    const task = tasks.find((t) => t.id === taskId);
    if (!task || task.status === newStatus) return;

    try {
      const data = await api.put<{ success: boolean; task: Task }>('/tasks', {
        id: taskId,
        status: newStatus,
      });
      if (data.success) {
        updateTask(taskId, { status: newStatus });
      }
    } catch (error) {
      console.error("Error updating task status:", error);
    }
  };

  const handleAddTask = (status: TaskStatus) => {
    setSelectedTask(null);
    setTaskDialogOpen(true);
  };

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setTaskDialogOpen(true);
  };

  const tasksByStatus = COLUMNS.reduce((acc, col) => {
    acc[col.id] = tasks
      .filter((t) => t.status === col.id)
      .sort((a, b) => a.order - b.order);
    return acc;
  }, {} as Record<TaskStatus, Task[]>);

  const selectedProject = projects.find((p) => p.id === selectedProjectId);

  const handleProjectChange = (projectId: string) => {
    setRefreshKey(k => k + 1);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold">Tareas</h1>
          {selectedProject && (
            <span
              className="px-2 py-0.5 rounded text-xs font-medium"
              style={{ backgroundColor: selectedProject.color || "#6366f1", color: "white" }}
            >
              {selectedProject.name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <ProjectsMenu onProjectChange={handleProjectChange} />
          <Button
            onClick={() => handleAddTask("PENDING")}
            size="sm"
            className="gap-1"
          >
            <Plus className="w-4 h-4" />
            Nueva Tarea
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-x-auto p-4">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-4 h-full">
            {COLUMNS.map((column) => (
              <KanbanColumn
                key={column.id}
                id={column.id}
                title={column.title}
                tasks={tasksByStatus[column.id]}
                onAddTask={() => handleAddTask(column.id)}
                onTaskClick={handleTaskClick}
              />
            ))}
          </div>

          <DragOverlay>
            {activeTask && <TaskCard task={activeTask} />}
          </DragOverlay>
        </DndContext>
      </div>

      <TaskDialog
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        task={selectedTask}
        projectId={selectedProjectId}
        defaultStatus={
          selectedTask?.status ||
          (tasks.length === 0 ? "PENDING" : undefined)
        }
        onSuccess={() => {
          loadData();
          setTaskDialogOpen(false);
        }}
      />
    </div>
  );
}
